import React, {
  FunctionComponent,
  useEffect,
  useState,
  useMemo,
  useRef,
  useLayoutEffect,
} from 'react';
import { Rnd } from 'react-rnd';
import { uniqueId } from 'lodash';
import { usePageContext, pdfjs } from 'react-pdf';
import { Textfit } from 'react-textfit';
import {
  Change,
  AnnotationLike,
  isAnnotationChange,
  isAnnotationAdd,
  AnnotationAddMetaType,
  AnnotationAddType,
} from './types';
import { normalizeFieldType } from './FormPDFEditorSideBar';
import { getBestSelectionRectangle } from './smartAdd';

// Fill in some missing type props for PDFAnnotationData
declare module 'pdfjs-dist' {
  interface PDFAnnotationData {
    alternativeText?: string;
    annotationType?: number;
    borderStyle?: object;
    comb?: boolean;
    fieldFlags?: number;
    fieldName?: string;
    fieldType?: string;
    fieldValue?: string;
    id?: string;
    maxLen?: number | null;
    multiLine?: boolean;
    readOnly?: boolean;
    textAlignment?: number;
    rect: [number, number, number, number];
  }
}

declare module 'pdfjs-dist/types/src/display/display_utils' {
  interface PageViewport {
    // the typings for this function do not seem to agree with whats actually
    // being returned in this file
    convertToPdfPoint(x: number, y: number): [number, number];
  }
}

interface Props {
  pageNumber: number;
  scale: number;
  addAnnotation: AnnotationAddMetaType | undefined;
  smartAddAnnotation: boolean;
  setAddAnnotation: (value: AnnotationAddMetaType | undefined) => void;
  selectedAnnotation: AnnotationLike | undefined;
  onAnnotationSelected: (annotation: AnnotationLike) => void;
  changes: Change[];
  recordChange: (change: Change) => void;
}

// Get unique signing name for annotation.
// Signatures have an extended name
const getAnnotationFieldName = (addAnnotation: AnnotationAddMetaType) => {
  if (addAnnotation.type === AnnotationAddType.SIGNATURE) {
    return `${AnnotationAddType.SIGNATURE}_${addAnnotation.signer}_${addAnnotation.signature}`;
  }
  return addAnnotation.type;
};

export const FormAnnotationLayer: FunctionComponent<Props> = ({
  pageNumber,
  scale,
  addAnnotation,
  smartAddAnnotation,
  setAddAnnotation,
  selectedAnnotation,
  onAnnotationSelected,
  changes,
  recordChange,
}) => {
  const pageContext = usePageContext();
  const page = pageContext?.page as pdfjs.PDFPageProxy | undefined;
  const pageScale = pageContext?.scale ?? 1;
  const [annotations, setAnnotations] = useState<pdfjs.PDFAnnotationData[]>([]);

  const viewport = useMemo(
    () => page?.getViewport({ scale: pageScale }),
    [page, pageScale],
  );

  // retrieve annotations for the current page
  useEffect(() => {
    page
      ?.getAnnotations()
      .then((newAnnotations) =>
        setAnnotations(newAnnotations as unknown as pdfjs.PDFAnnotationData[]),
      );
  }, [page]);

  const dragStartPosition = useRef({ x: 0, y: 0 });

  const renderAnnotation = (annotation: AnnotationLike) => {
    // get any pending changes that can override the changes saved in the pdf
    const currentChange = changes.find(({ id }) => id === annotation.id);

    // don't render deleted annotations
    if (
      currentChange &&
      isAnnotationChange(currentChange) &&
      currentChange.deleted
    ) {
      return null;
    }

    // [x1, y1, x2, y2]
    const annotationRect = currentChange?.rect ?? annotation.rect;
    // this conversion flips the coordinate system
    // x1 is the left most x value, x2 is the right most x value
    // y1 is the bottom most y value, y2 is the top most y value
    const displayRect = viewport?.convertToViewportRectangle(annotationRect);
    const className = ['annotation'];
    let isSelected = false;
    if (
      annotation.id &&
      selectedAnnotation?.id &&
      annotation.id === selectedAnnotation.id
    ) {
      className.push('selected');
      isSelected = true;
    }

    const x = displayRect?.[0] ?? 0;
    const y = displayRect?.[3] ?? 0;
    const width = (displayRect?.[2] ?? 0) - x;
    const height = (displayRect?.[1] ?? 0) - y;

    return (
      <Rnd
        key={annotation.id}
        className={`annotation-container annotation-${
          normalizeFieldType(annotation.fieldType) ?? ''
        }`}
        style={{
          zIndex: isSelected ? 1 : undefined,
        }}
        minWidth={1}
        minHeight={1}
        scale={scale}
        size={{ width, height }}
        resizeGrid={[0.25, 0.25]}
        enableResizing={{
          bottomRight: isSelected,
        }}
        resizeHandleStyles={{
          bottomRight: {
            width: 3,
            height: 3,
            right: -2,
            bottom: -2,
            backgroundColor: '#1976d2',
          },
        }}
        position={{ x, y }}
        disableDragging={!isSelected}
        bounds="parent"
        onDragStart={(e, d) => {
          // record where the drag started
          const [startX, startY] = viewport?.convertToPdfPoint(d.x, d.y) ?? [
            0, 0,
          ];
          dragStartPosition.current.x = startX;
          dragStartPosition.current.y = startY;
        }}
        onDrag={(event) => {
          // block document pan during drag
          event.preventDefault();
          event.stopPropagation();
        }}
        onDragStop={(e, d) => {
          // use the difference between the drag start and the drag end to compute a drag delta
          const [endX, endY] = viewport?.convertToPdfPoint(d.x, d.y) ?? [0, 0];
          const deltaX = endX - dragStartPosition.current.x;
          const deltaY = endY - dragStartPosition.current.y;
          // save the drag delta as a change record
          if (deltaX || deltaY) {
            recordChange({
              type: 'change',
              id: annotation.id ?? '',
              originalFieldName: annotation.fieldName ?? '',
              rect: [
                annotationRect[0] + deltaX,
                annotationRect[1] + deltaY,
                annotationRect[2] + deltaX,
                annotationRect[3] + deltaY,
              ],
            });
          }
        }}
        onResizeStop={(e, dir, ref, d) => {
          // calculate the new bottom right corner based on the width and height deltas
          const [endX2, endY2] = viewport?.convertToPdfPoint(
            (displayRect?.[2] ?? 0) + d.width,
            (displayRect?.[1] ?? 0) + d.height,
          ) ?? [0, 0];
          // save the resize as a change record
          recordChange({
            type: 'change',
            id: annotation.id ?? '',
            originalFieldName: annotation.fieldName ?? '',
            rect: [annotationRect[0], endY2, endX2, annotationRect[3]],
          });
        }}
      >
        <Textfit
          mode="single"
          forceSingleModeWidth={false}
          className={className.join(' ')}
          style={{ width, height }}
          onMouseUp={() => {
            onAnnotationSelected(annotation);
          }}
        >
          {currentChange?.fieldName ?? annotation.fieldName}
        </Textfit>
      </Rnd>
    );
  };

  const buildHighlightRef = useRef<HTMLDivElement>(null);
  const smartBuildHighlightRef = useRef<HTMLDivElement>(null);
  interface ElementPosition {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  }
  const newElementPositionRef = useRef<ElementPosition>();
  const smartElementPositionRef = useRef<ElementPosition>();

  // rendering this entire component is expensive
  // so to reduce overhead of showing a highlight of a new annotation being placed
  // its dynamic properties are directly mutated in the dom instead of through react state
  const refreshBuildHighlightStyle = (
    highlightRef: React.RefObject<HTMLDivElement>,
    positionRef: React.RefObject<ElementPosition | undefined>,
  ) => {
    if (!highlightRef.current) return;

    if (!positionRef.current) {
      // hide the build highlight when it's not in use
      highlightRef.current.style.display = 'none';
    } else {
      const { startX, startY, endX, endY } = positionRef.current;
      // calculate the build highlight location by computing a box from
      // the upper left point to the lower right point of the current selection
      const top = Math.min(startY, endY);
      const left = Math.min(startX, endX);
      const width = Math.abs(endX - startX);
      const height = Math.abs(endY - startY);
      highlightRef.current.style.display = 'unset';
      highlightRef.current.style.top = `${top}px`;
      highlightRef.current.style.left = `${left}px`;
      highlightRef.current.style.width = `${width}px`;
      highlightRef.current.style.height = `${height}px`;
    }
  };
  const refreshAllBuildHighlights = () => {
    refreshBuildHighlightStyle(buildHighlightRef, newElementPositionRef);
    refreshBuildHighlightStyle(smartBuildHighlightRef, smartElementPositionRef);
  };
  const clearAllBuildHighlights = () => {
    newElementPositionRef.current = undefined;
    smartElementPositionRef.current = undefined;
    refreshAllBuildHighlights();
  };
  // reapply dom mutations on react render
  useLayoutEffect(refreshAllBuildHighlights);

  // clear the build highlight if an element is no longer being added
  useEffect(() => {
    if (!addAnnotation) newElementPositionRef.current = undefined;
  }, [addAnnotation]);

  const allAnnotations = [
    ...annotations,
    ...changes
      .filter(isAnnotationAdd)
      .filter((c) => c.pageIndex === pageNumber),
  ];
  return (
    <div
      className="form-annotation-layer"
      style={{
        width: `${viewport?.width}px`,
        height: `${viewport?.height}px`,
        transform: `translate(-50%, -50%) rotate(${page?.rotate}deg)`,
      }}
      onMouseDown={(event) => {
        if (!addAnnotation) return;
        event.preventDefault();
        event.stopPropagation();
        const bounds = event.currentTarget.getBoundingClientRect();
        const startX = (event.nativeEvent.clientX - bounds.x) / scale;
        const startY = (event.nativeEvent.clientY - bounds.y) / scale;
        newElementPositionRef.current = {
          startX,
          startY,
          endX: startX,
          endY: startY,
        };
        refreshAllBuildHighlights();
      }}
      onMouseMove={(event) => {
        if (!addAnnotation || !newElementPositionRef.current) {
          // clear eveything in case addAnnotation was cleared when the user was dragging a new box
          clearAllBuildHighlights();
          return;
        }

        event.persist();
        event.preventDefault();
        event.stopPropagation();
        // handle dragging when the left mouse button is pressed
        if (event.buttons === 1) {
          // get the drag box from the initial and current mouse position
          const bounds = event.currentTarget.getBoundingClientRect();
          const endX = (event.nativeEvent.clientX - bounds.x) / scale;
          const endY = (event.nativeEvent.clientY - bounds.y) / scale;
          newElementPositionRef.current.endX = endX;
          newElementPositionRef.current.endY = endY;

          // if smart add annotation is enabled, attempt to get a better annotation size
          const { startX, startY } = newElementPositionRef.current;
          try {
            if (smartAddAnnotation) {
              const canvas = document.querySelector<HTMLCanvasElement>(
                `div[data-page-number="${pageNumber}"] canvas`,
              );
              if (canvas) {
                const canvasXScale = canvas.width / (viewport?.width ?? 1);
                const canvasYScale = canvas.height / (viewport?.height ?? 1);

                const smartSelection = getBestSelectionRectangle({
                  canvas,
                  canvasXScale,
                  canvasYScale,
                  startX,
                  startY,
                  endX,
                  endY,
                });

                // update the smart build highlight
                const top = Math.min(startY, endY);
                const left = Math.min(startX, endX);
                smartElementPositionRef.current = smartSelection
                  ? {
                      startX: left + smartSelection.x,
                      startY: top + smartSelection.y,
                      endX: left + smartSelection.x + smartSelection.width,
                      endY: top + smartSelection.y + smartSelection.height,
                    }
                  : newElementPositionRef.current;
              }
            }
          } catch (err) {
            // noop
          }

          // refresh all highlights with the new positions
          refreshAllBuildHighlights();
        }
      }}
      onMouseUp={(event) => {
        if (!addAnnotation || !newElementPositionRef.current) {
          // clear eveything in case addAnnotation was cleared when the user was dragging a new box
          clearAllBuildHighlights();
          return;
        }
        event.preventDefault();
        event.stopPropagation();

        // convert the build highlist to a pdf position
        const { startX, startY, endX, endY } =
          smartElementPositionRef.current ?? newElementPositionRef.current;
        const [pdfStartX, pdfStartY] = viewport?.convertToPdfPoint(
          startX,
          startY,
        ) ?? [0, 0];
        const [pdfEndX, pdfEndY] = viewport?.convertToPdfPoint(endX, endY) ?? [
          0, 0,
        ];

        // save the new annotation
        recordChange({
          type: 'add',
          id: uniqueId(),
          pageIndex: pageNumber,
          fieldType: addAnnotation.type,
          fieldName: `${getAnnotationFieldName(addAnnotation)}_${
            allAnnotations.length
          }`,
          // x1 is the left most x value, x2 is the right most x value
          // y1 is the bottom most y value, y2 is the top most y value
          rect: [
            Math.min(pdfStartX, pdfEndX), // x1
            Math.min(pdfStartY, pdfEndY), // y1
            Math.max(pdfStartX, pdfEndX), // x2
            Math.max(pdfStartY, pdfEndY), // y2
          ],
        });

        // clear all highlights and the add action
        clearAllBuildHighlights();
        setAddAnnotation(undefined);
      }}
    >
      {allAnnotations.map(renderAnnotation)}
      <div
        ref={buildHighlightRef}
        className={
          smartAddAnnotation
            ? 'annotation-build-highlight-outline'
            : 'annotation-build-highlight'
        }
      />
      <div
        ref={smartBuildHighlightRef}
        className="annotation-build-highlight"
      />
    </div>
  );
};
